import { FieldPolicy } from '@apollo/client';
import {
  Analytics,
  AnnotationMap,
  Channel,
  Dataset,
  DatasetJob,
  GanModel,
  GetVolumeDataQuery,
  Graph,
  MlInference,
  MlModel,
  Umap,
  Volume,
  Workspace,
} from '@generated/UseGraphqlHooks';

export const QueryPolicies = {
  getAnalytics: {
    keyArgs: ['workspaceId', 'datasetId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('analyticsId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Analytics>, Analytics[]>,
  getAnnotationMaps: {
    keyArgs: ['organizationId', 'workspaceId', 'mapId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('mapId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, AnnotationMap>, AnnotationMap[]>,
  getChannels: {
    keyArgs: ['organizationId', 'workspaceId', 'channelId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('channelId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Channel>, Channel[]>,
  getDatasetFiles: {
    keyArgs: ['workspaceId', 'datasetId', 'path'],
    merge(existing, incoming) {
      if (!existing) {
        return incoming;
      }
      return [...existing, ...incoming];
    },
  } as FieldPolicy<string[], string[]>,
  getDatasetJobs: {
    keyArgs: ['organizationId', 'workspaceId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('datasetId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, DatasetJob>, DatasetJob[]>,
  getDatasets: {
    keyArgs: ['workspaceId', 'datasetId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('datasetId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Dataset>, Dataset[]>,
  getGANModels: {
    keyArgs: ['organizationId', 'workspaceId', 'modelId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('modelId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, GanModel>, GanModel[]>,
  getGraphs: {
    keyArgs: ['workspaceId', 'graphId', 'staged', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('graphId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Graph>, Graph[]>,
  getMLModels: {
    keyArgs: ['workspaceId', 'datasetId', 'modelId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('modelId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, MlModel>, MlModel[]>,
  getMLInferences: {
    keyArgs: ['workspaceId', 'modelId', 'datasetId', 'inferenceId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('inferenceId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, MlInference>, MlInference[]>,
  getUMAPs: {
    keyArgs: ['workspaceId', 'datasetId', 'umapId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        if (item) {
          merged[String(readField('umapId', item))] = item;
        }
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Umap>, Umap[]>,
  getVolumeData: {
    keyArgs: ['volumeId', 'dir', 'keys'],
    merge(existing, incoming) {
      if (!existing) {
        return incoming;
      }
      return {
        keys: [...existing.keys, ...incoming.keys],
        pageInfo: incoming.pageInfo,
      };
    },
  } as FieldPolicy<GetVolumeDataQuery['getVolumeData'], GetVolumeDataQuery['getVolumeData']>,
  getVolumes: {
    keyArgs: ['organizationId', 'workspaceId', 'volumeId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('volumeId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Volume>, Volume[]>,
  getWorkspaces: {
    keyArgs: ['organizationId', 'workspaceId', 'filters'],
    merge(existing, incoming, { readField }) {
      const merged = { ...existing };
      incoming.forEach((item) => {
        merged[String(readField('workspaceId', item))] = item;
      });
      return merged;
    },
    read(existing) {
      return existing && Object.values(existing);
    },
  } as FieldPolicy<Record<string, Workspace>, Workspace[]>,
};
